const PUB_SUB_EVENTS = {
  cartUpdate: "cart-update",
  cartChange: "cart-change",
  cartAdd: "cart-add",
  quantityUpdate: "quantity-update",
  optionValueSelectionChange: "option-value-selection-change",
  variantChange: "variant-change",
  cartError: "cart-error",
};

function handleEventListeners(events = [], type = "add") {
  const evts = Array.isArray(events) ? events : [events];
  for (const evt of evts) {
    if (type === "add") {
      evt.el.addEventListener(evt.type, evt.callback);
    } else {
      evt.el.removeEventListener(evt.type, evt.callback);
    }
  }
}

async function updateCart(data) {
  const { type, source, items, submitter, sections, target, sections_url } = data;

  const body = {};

  if (type === "add") {
    body.items = items;
  } else if (type === "update") {
    const updates = {};
    for (const item of items) {
      updates[item.id] = item.quantity;
    }
    body.updates = updates;
  }
  const product_ids = items.map((item) => {
    return item.product_id;
  });
  if (submitter) {
    submitter.classList.add("btn-elem--loading");
  }
  const critical_sections = ["header"];
  if (sections) {
    body.sections = [...critical_sections, ...sections].slice(0, 5).join(",");
  } else {
    body.sections = critical_sections;
  }
  try {
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    };
    const response = await fetch(`/cart/${type}.js`, options);
    const data = await response.json();
    if (_learnq && type === "add") {
      pushKlaviyo(data);
    }
    if (!response.ok) {
      throw new Error("Something went wrong");
    }
    const sections = {};
    for (const [key, value] of Object.entries(data.sections)) {
      const html = new DOMParser().parseFromString(value, "text/html");
      sections[key] = html;
    }
    publish(PUB_SUB_EVENTS.cartUpdate, {
      data: {
        type,
        source,
        response: data,
        sections,
        submitter,
        product_ids,
        target,
      },
    });
  } catch (error) {
    console.log(error);
    publish(PUB_SUB_EVENTS.cartError, {
      data: { message: error },
    });
  } finally {
    if (submitter) {
      submitter.classList.remove("btn-elem--loading");
    }
  }
}

class CartLines extends HTMLElement {
  constructor() {
    super();

    this.changing_lines = [];
    this.items_wrapper = this.querySelector(".cart-lines__items");
  }

  connectedCallback() {
    this.addEventListeners();
    this.quantityChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.updateQuantity.bind(this),
    );
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.renderLines.bind(this));
  }

  disconnectedCallback() {
    this.quantityChangeUnsubscriber();
    this.cartUpdateUnsubscriber();
  }

  addEventListeners() {
    this.addEventListener("click", this.clickHandler.bind(this));
  }

  clickHandler(e) {
    if (e.target.classList.contains("cart-line__remove")) {
      e.preventDefault();
      this.removeLine(e);
    }
  }

  updateQuantity({ data }) {
    const { quantity, target } = data;

    if (this.contains(target)) {
      const line = target.closest("[data-line]");
      if (!line) return;
      this.querySelector(
        `[data-line="${line.dataset.line}"] quantity-selector [name="quantity"]`,
      ).value = quantity;
      this.updateCartLine(line, quantity);
    }
  }

  updateCartLine(line, quantity) {
    const data = {
      items: [
        {
          id: `${line.dataset.line}`,
          quantity,
          product_id: line.querySelector("quantity-selector").dataset.productId,
        },
      ],
      source: "cart-lines",
      type: "update",
    };
    if (window.location.pathname.includes("/cart")) {
      const section_id = document.querySelector("main-cart").dataset.section;
      data.sections = [`${section_id}`];
    }
    updateCart(data);
  }

  renderLines({ data }) {
    const { source, response, sections } = data;
    if (
      source.toUpperCase() !== this.nodeName ||
      this.querySelector(".cart-lines__items").children.length !== response.items.length
    ) {
      this.updateLinesHTML(data);
    } else {
      for (const line of response.items) {
        const selector = `[data-line="${line.key}"] .cart-line__price .cart-line__price-display`;
        if (
          !this.querySelector(selector) ||
          line.quantity !== +this.querySelector(`[data-line="${line.key}"] .quantity__input`).value
        ) {
          this.updateLinesHTML(data);
          break;
        } else {
          this.querySelector(selector).innerHTML =
            sections.header.querySelector(selector).innerHTML;
        }
      }
    }
  }

  updateLinesHTML(data) {
    const { source, response, sections } = data;
    if (!sections.header) return;
    if (response.items.length > 0) {
      const lines = sections.header.querySelector(".cart-lines__items");
      const old_lines = this.querySelector(".cart-lines__items");
      old_lines.innerHTML = lines.innerHTML;
    } else {
      this.querySelector(".cart-lines__items").innerHTML = "";
    }
  }

  removeLine(e) {
    const line = e.target.closest("[data-line]");
    const data = {
      items: [
        {
          id: `${line.dataset.line}`,
          quantity: 0,
          product_id: line.querySelector("quantity-selector").dataset.productId,
        },
      ],
      source: "cart-lines",
      type: "update",
    };
    line.classList.add("cart-line__removing");
    if (window.location.pathname.includes("/cart")) {
      const section_id = document.querySelector("main-cart").dataset.section;
      data.sections = [`${section_id}`];
    }
    setTimeout(() => {
      line.remove();
      updateCart(data);
    }, 1000);
  }
}

customElements.define("cart-lines", CartLines);

class CartDropdown extends DropdownMenu {
  constructor() {
    super();
    this.toast_timer = null;
    this.toast_interval = 100;
    this.toast_age = 3000;
    this.toast_remaining = this.toast_age;
    this.toast_paused = false;
    this.toast_container = this.querySelector("[dropdown-action-toast]");
    this.toast_icon = this.querySelector(".toast__icon");
    this.toast_message = this.querySelector(".toast__message");
    this.toast_clock = this.querySelector(".toast__timer--inner");
    this.maxHeightHandler();
    this.addCartEventListeners();
  }
  connectedCallback() {
    super.connectedCallback();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.updateCart.bind(this));
    this.toastUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.showToast.bind(this));
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.cartUpdateUnsubscriber();
    this.toastUnsubscriber();
  }

  addCartEventListeners() {
    window.addEventListener("resize", this.maxHeightHandler.bind(this));
    if (!this.is_touch) {
      this.addEventListener("mousemove", this.mouseHandler.bind(this));
      this.toast_container.addEventListener("mouseenter", this.pauseToast.bind(this));
      this.toast_container.addEventListener("mouseleave", this.startToast.bind(this));
    }
  }

  maxHeightHandler() {
    const dropdown = this.querySelector(".dropdown-menu__list");
    const dropdown_position = dropdown.getBoundingClientRect().top;
    dropdown.style.maxHeight = window.innerHeight - dropdown_position - 10 + "px";
  }

  outsideClickHandler(e) {
    if (e.target.closest("cart-dropdown") !== this) {
      if (this.is_open) {
        e.preventDefault();
        this.setHidden();
      }
    }
  }

  mouseHandler(e) {
    if (e.type === "mouseenter" && this.getAttribute("dropdown-toast") === "hidden") {
      this.setVisible();
    } else if (e.type === "mouseleave") {
      this.toggle_button.blur();
      this.setHidden();
    } else if (e.type === "mousemove" && !e.target.closest(".dropdown-menu__toast")) {
      this.setVisible();
      this.resetToast();
    }
  }

  showToast({ data }) {
    if (data.type !== "add" && !data.message) return;
    this.resetToast();
    if (data.message) {
      this.toast_icon.classList.add("toast__error");
      this.toast_message.innerHTML = `${data.message}`;
    } else {
      const { response } = data;
      this.toast_icon.classList.add("toast__success");
      if (response.items.length > 1) {
        this.toast_message.innerHTML = `<span>Multiple items</span> were added to your basket`;
      } else {
        const title = response.items[0].title;
        this.toast_message.innerHTML = `<span>${title}</span> was added to your basket`;
      }
    }
    this.toast_container.setAttribute("role", "alert");
    this.setAttribute("dropdown-toast", "visible");
    this.startToast();
  }

  pauseToast(e) {
    clearInterval(this.toast_timer);
    this.toast_timer = null;
  }

  startToast(e) {
    if (this.toast_remaining <= 0) {
      this.toast_remaining = this.toast_age;
    }
    this.toast_timer = setInterval(() => {
      this.updateToast();
    }, this.toast_interval);
  }

  updateToast() {
    if (this.toast_remaining <= 0) {
      this.resetToast();
    }
    this.toast_remaining = this.toast_remaining - this.toast_interval;
    const percentage = (100 / this.toast_age) * this.toast_remaining;
    this.toast_clock.style.width = `${percentage}%`;
  }

  resetToast() {
    clearInterval(this.toast_timer);
    this.toast_timer = null;
    this.setAttribute("dropdown-toast", "hidden");
    this.toast_clock.removeAttribute("style");
    this.toast_container.removeAttribute("role");
    this.toast_icon.className = "toast__icon";
    this.toast_remaining = this.toast_age;
  }

  updateCart({ data }) {
    const { response, sections } = data;
    if (sections.header) {
      const total = sections.header.querySelector(".dropdown-menu__price").innerHTML;
      this.updateTotals(total);
    }

    if (response.items.length <= 0) {
      this.classList.add("site-header__cart-empty");
      this.querySelector(".dropdown-menu__icon").classList.remove("dropdown-menu__expand-button");
    } else {
      this.classList.remove("site-header__cart-empty");
      this.querySelector(".dropdown-menu__icon").classList.add("dropdown-menu__expand-button");
    }
  }

  updateTotals(new_total) {
    const totals = this.querySelectorAll(".dropdown-menu__price");
    for (const total of totals) {
      total.innerHTML = new_total;
    }
  }
}
customElements.define("cart-dropdown", CartDropdown);

class ClearCart extends ModalDialog {
  constructor() {
    super();
  }
  clickHandler(e) {
    super.clickHandler(e);
    e.preventDefault();
    if (
      e.target.closest("[modal-dialog-event]")?.getAttribute("modal-dialog-event") === "clear-cart"
    ) {
      this.clearCart(e);
    }
  }
  async clearCart() {
    const data = {
      items: [],
      source: "clear-cart",
      type: "clear",
    };
    await updateCart(data);
    this.closeModal();
  }
}

customElements.define("clear-cart", ClearCart);

class ShippingBar extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      this.checkEligiblity.bind(this),
    );
  }
  disconnectedCallback() {
    this.cartUpdateUnsubscriber();
  }

  checkEligiblity({ data }) {
    const { sections } = data;
    this.innerHTML = sections.header.querySelector("shipping-bar").innerHTML;
    this.setAttribute(
      "shipping-bar-eligible",
      sections.header.querySelector("shipping-bar").getAttribute("shipping-bar-eligible"),
    );
  }
}

customElements.define("shipping-bar", ShippingBar);

class SiteSearch extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector("#site-search-dialog");
    this.search_button = this.querySelector('[data-action="open"]');
    this.close_button = this.querySelector('.site-search__close[data-action="close"]');
    this.search_height = this.getBoundingClientRect().height;
    this.input = this.querySelector('form input[type="search"]');
    // Prevent bfcache from prefilling search
    window.addEventListener("pageshow", (e) => {
      if (e.persisted) {
        this.dialog.close();
        this.input.value = "";
        this.input.dispatchEvent(new Event("input"));
      }
    });
    this.results_container = this.querySelector(".site-search__results-container");

    this.search = {
      query: this.getAttribute("search-query"),
      type: this.dataset.searchType,
      limit: this.dataset.maxResults,
      scope: "each",
      fields: this.dataset.searchFields,
      unavailable: this.dataset.unavailableProducts,
    };

    this.events = [
      {
        el: this,
        type: "click",
        callback: this.clickHandler.bind(this),
      },
      {
        el: this.input,
        type: "input",
        callback: this.inputHandler.bind(this),
      },
      {
        el: document,
        type: "keydown",
        callback: this.keyboardHandler.bind(this),
      },
    ];
  }

  connectedCallback() {
    this.classList.remove("search-loading");
    this.input.closest(".site-search__input-container").style.height = this.search_height + "px";
    if (this.dataset.animatePlaceholder) {
      this.animatePlaceholder();
    }
    handleEventListeners(this.events);
  }

  clickHandler(e) {
    if (this.search_button.contains(e.target)) {
      this.dialog.show();
    }
    if (e.target.closest('[data-action="close"]')) {
      this.dialog.close();
    }
  }

  keyboardHandler(e) {
    if (e.key === "Escape") {
      this.dialog.close();
    }
  }
  inputHandler(e) {
    this.debounce(() => this.setSearchQuery(e));
  }

  setSearchQuery(e) {
    this.setAttribute("search-query", e.target.value);
  }

  attributeChangedCallback(name, prev, next) {
    if (prev === next) return;
    if (next === "") {
      this.querySelector("[data-results]").dataset.results = "false";
    } else {
      this.fetchSearchResults(next);
    }
  }

  static get observedAttributes() {
    return ["search-query"];
  }

  async fetchSearchResults(query) {
    this.results_container.classList.add("results--loading");
    const string = `/search/suggest?q=${query}&resources[type]=${this.search.type}&resources[limit]=${this.search.limit}&resources[limit_scope]=${this.search.scope}&resources[options][fields]=${this.search.fields}&resources[options][unavailable_products]=${this.search.unavailable}&section_id=site-predictive-search`;

    try {
      const response = await fetch(string);
      if (!response.ok) {
        throw new Error(response.status);
      }
      const data = await response.text();
      const results = new DOMParser()
        .parseFromString(data, "text/html")
        .querySelector("#shopify-section-site-predictive-search");
      this.renderSearchResults(results);
    } catch (err) {
      console.log(err);
    } finally {
      this.results_container.classList.remove("results--loading");
    }
  }

  renderSearchResults(html) {
    const no_results = html.querySelector(".column--noresults");
    if (no_results) {
      this.querySelector(".column--noresults").innerHTML =
        html.querySelector(".column--noresults").innerHTML;
      this.querySelector("[data-results]").dataset.results = "none";
    } else {
      if (html.querySelector(".column--links")) {
        this.querySelector(".column--links").innerHTML =
          html.querySelector(".column--links").innerHTML;
      } else {
        this.querySelector(".column--links").innerHTML = "";
      }
      if (html.querySelector(".column--products")) {
        this.querySelector(".column--products").innerHTML =
          html.querySelector(".column--products").innerHTML;
      } else {
        this.querySelector(".column--products").innerHTML = "";
      }
      this.querySelector("[data-results]").dataset.results = "true";
    }
  }

  animatePlaceholder() {
    const placeholder_container = this.querySelector("[data-placeholder-strings]");

    if (placeholder_container) {
      const placeholder_values = placeholder_container.dataset.placeholderStrings.split("|||");
      if (placeholder_values.length > 1) {
        this.animation_string_index = 0;
        this.animation_value_index = placeholder_values[this.animation_string_index].length;
        this.delete = true;
        this.animation_speed = +this.dataset.animationSpeed || 100;
        this.delete_speed = +this.dataset.deleteSpeed || 50;
        this.animation_delay = +this.dataset.animationDelay || 1000;
        console.log(this.animation_speed, this.delete_speed, this.animation_delay);
        setTimeout(() => {
          this.typingEffect(placeholder_container, placeholder_values);
        }, this.animation_delay);
      }
    }
  }

  typingEffect(container, values) {
    const current_string = values[this.animation_string_index];
    let pause = false;
    if (this.delete && this.animation_value_index >= 0) {
      container.innerHTML = current_string.substr(0, this.animation_value_index);
      this.animation_value_index--;
    } else if (this.animation_value_index === -1) {
      this.animation_string_index + 1 >= values.length
        ? (this.animation_string_index = 0)
        : this.animation_string_index++;
      this.delete = !this.delete;
      this.animation_value_index = 0;
    } else if (this.animation_value_index < current_string.length) {
      container.innerHTML = current_string.substr(0, this.animation_value_index + 1);
      this.animation_value_index++;
    } else {
      pause = true;
      this.delete = !this.delete;
    }
    setTimeout(
      () => {
        this.typingEffect(container, values);
      },
      pause ? this.animation_delay : this.delete ? this.delete_speed : this.animation_speed,
    );
  }

  debounce(fn, wait = 500) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      fn.apply(this);
    }, wait);
  }
}
customElements.define("site-search", SiteSearch);

class SearchHistory extends HTMLElement {
  constructor() {
    super();

    this.search_history = localStorage.getItem("search-history")
      ? JSON.parse(localStorage.getItem("search-history"))
      : [];
  }

  connectedCallback() {
    if (this.search_history.length > 0) {
      this.renderSearchHistory();
    }
    const query = new URLSearchParams(window.location.search).get("q");
    if (!query) return;

    const ind = this.search_history.findIndex((value) => value.query === query);
    if (ind === -1) {
      this.storeQuery(query);
    } else if (ind > 0) {
      this.search_history.unshift(this.search_history.splice(ind, 1)[0]);
      localStorage.setItem("search-history", JSON.stringify(this.search_history));
    }
  }
  async storeQuery(q) {
    const string = `/search/suggest.json?q=${q}&resources[type]=product&resources[limit]=4&resources[options][fields]=title,product_type,variants.title,variants.sku,variants.barcode,tag`;

    try {
      const response = await fetch(string);
      if (!response.ok) {
        throw new Error(response.status);
      }
      const data = await response.json();
      if (data.resources?.results?.products?.length > 0) {
        this.search_history.unshift({
          query: q,
          products: data.resources.results.products,
        });
        if (this.search_history.length >= +this.dataset.maxProducts - 1) {
          this.search_history = this.search_history.splice(0, +this.dataset.maxProducts);
        }
      }
    } catch (err) {
      console.log(err);
    }

    localStorage.setItem("search-history", JSON.stringify(this.search_history));
  }
  renderSearchHistory() {
    const container = this.querySelector(".site-predictive-search__list");
    for (const item of this.search_history) {
      const li = document.createElement("li");
      li.classList.add("site-predictive-search__list-item");
      const link = document.createElement("a");
      link.href = `/search?q=${item.query}&type=product`;
      link.classList.add("search-history__item");
      const img_grid = document.createElement("div");
      img_grid.classList.add("search-history__images");
      for (const prod of item.products) {
        const img = document.createElement("img");
        img.classList.add("search-history__image");
        img.src = prod.image + "&width=300";
        img_grid.appendChild(img);
      }
      const title = document.createElement("span");
      title.innerText = item.query;

      link.appendChild(img_grid);
      link.appendChild(title);
      li.appendChild(link);
      container.appendChild(li);
    }
    this.classList.add("search-history--loaded");
  }
}
customElements.define("search-history", SearchHistory);

class VariantSelect extends HTMLElement {
  constructor() {
    super();

    this.variant_json = this.querySelector("[data-variant-json]")
      ? JSON.parse(this.querySelector("[data-variant-json]").innerHTML)
      : null;
    this.events = [
      {
        el: this,
        type: "input",
        callback: this.variantChanged.bind(this),
      },
    ];
  }

  connectedCallback() {
    handleEventListeners(this.events, "add");
  }
  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
  }

  variantChanged(e) {
    if (!this.variant_json) return;

    const values = this.selectedOptionValues;
    const target = e.target.tagName === "SELECT" ? e.target.selectedOptions[0] : e.target;

    this.updateSelectedTitle(e);

    publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
      data: { event: e, target, selectedOptionValues: values },
    });
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll("select option[selected], fieldset input:checked")).map(
      ({ dataset }) => dataset.optionValueId,
    );
  }

  updateSelectedTitle({ target }) {
    const { value } = target;
    const selectedOptionTitle = target
      .closest(`.variant-select__group`)
      .querySelector("[data-selected-value]");
    if (selectedOptionTitle) selectedOptionTitle.innerHTML = value;
  }
}

customElements.define("variant-select", VariantSelect);

class QuantityModule extends HTMLElement {
  constructor() {
    super();
    this.product_id = this.dataset.productId;
    this.section_id = this.dataset.sectionId;
    this.quantity = this.getCurrentQuantity;
    this.multiples = +this.dataset.multiples || 1;
    this.key = this.dataset.key || null;
  }

  parseEvent(details) {
    const { debounce, target } = details;

    const current_quantity = this.getCurrentQuantity;

    const quantity = current_quantity;
    // const quantity = this.validateQuantity(current_quantity);

    const data = {
      target,
      section_id: this.section_id,
      product_id: this.product_id,
      quantity,
    };

    if (debounce) {
      this.debounce(() => {
        publish(PUB_SUB_EVENTS.quantityUpdate, {
          data,
        });
      });
    } else {
      publish(PUB_SUB_EVENTS.quantityUpdate, {
        data,
      });
    }
  }

  get getCurrentQuantity() {
    return +this.querySelector('[name="quantity"]').value;
  }

  validateQuantity(quantity) {
    let validated_quantity = quantity;
    const remainder = quantity % this.multiples;
    if (remainder != 0) {
      validated_quantity = this.multiples - remainder + quantity;
    }
    return validated_quantity;
  }

  debounce(fn, wait = 500) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      fn.apply(this);
    }, wait);
  }
}
customElements.define("quantity-module", QuantityModule);

class QuantitySelector extends QuantityModule {
  constructor() {
    super();

    this.key = this.dataset.key || null;

    this.multiples = +this.dataset.multiples || this.quantity;
    this.apply_min = this.dataset.applyMinimum === "true" ? true : false;
    this.apply_debounce = this.dataset.debounce && this.dataset.debounce === "false" ? false : true;

    this.decrease = this.querySelector(".quantity__button--minus");
    this.increase = this.querySelector(".quantity__button--plus");
    this.quantityInput = this.querySelector(".quantity__input");
    this.quantityPrice = this.querySelector("[data-price]");

    this.events = [
      {
        type: "click",
        el: this,
        callback: this.clickHandler.bind(this),
      },
      {
        type: "change",
        el: this,
        callback: this.changeHandler.bind(this),
      },
    ];
  }

  clickHandler(e) {
    if (e.target == this.decrease || e.target == this.increase) {
      this.handleQuantityChange(e);
    }
  }

  changeHandler(e) {
    this.quantity = this.quantityInput.value;
    this.handleQuantityChange(e);
  }

  connectedCallback() {
    handleEventListeners(this.events, "add");
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
  }

  handleQuantityChange(e) {
    // this.quantity = +this.quantityInput.value;
    if (e.target == this.decrease) {
      if (this.apply_min) {
        if (this.quantity - this.multiples <= 0) return;
      } else {
        if (this.quantity - this.multiples < 0) return;
      }
      this.quantity = this.quantity - this.multiples;
    } else if (e.target == this.increase) {
      this.quantity = this.quantity + this.multiples;
    }
    this.quantity = this.validateQuantity(+this.quantity);
    this.quantityInput.value = this.quantity;

    const details = {
      debounce: this.apply_debounce, // debounce based on el
      target: e.target,
    };

    this.parseEvent(details);

    if (this.quantityPrice) {
      const price = +this.quantityPrice.dataset.price;
      if (!price) return;

      const dynamic_price = Shopify.formatMoney(price * this.quantity, theme.moneyFormat);
      this.quantityPrice.innerHTML = dynamic_price;
    }
  }
}

customElements.define("quantity-selector", QuantitySelector);

class QuantityDropdown extends QuantityModule {
  constructor() {
    super();

    this.key = this.dataset.key || null;

    this.multiples = +this.dataset.multiples || this.quantity;
    this.quantityInput = this.querySelector(".quantity__input");
    this.quantitySelection = this.querySelector(".quantity__selection");
    this.quantityDetails = this.querySelector(".quantity__details");

    this.events = [
      {
        type: "click",
        el: this,
        callback: this.clickHandler.bind(this),
      },
    ];
  }

  clickHandler(e) {
    if (e.target.classList.contains("quantity__item")) {
      this.handleQuantityChange(e);
    }
    if (
      e.target.classList.contains("quantity__menu-close") ||
      e.target.classList.contains("quantity__details")
    ) {
      this.quantityDetails.open = false;
    }
  }

  connectedCallback() {
    handleEventListeners(this.events, "add");
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
  }

  handleQuantityChange(e) {
    this.quantity = +e.target.dataset.value;
    this.quantityInput.value = this.quantity;
    this.quantitySelection.innerHTML = e.target.innerHTML;
    this.querySelector(".item__selected").classList.remove("item__selected");
    e.target.classList.add("item__selected");

    const details = {
      debounce: this.key ? true : false,
      target: e.target,
    };

    this.parseEvent(details);
    if (window.innerWidth > 767) {
      this.quantityDetails.open = false;
    }
  }
}

customElements.define("quantity-dropdown", QuantityDropdown);

class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.showQuantity =
      this.querySelector("quantity-selector") || this.querySelector("quantity-dropdown")
        ? true
        : false;

    this.currentLine = this.querySelector(".product-form__card")?.dataset.cartLine;
    this.cartForm = this.querySelector("cart-add-form");
    this.swatches = this.querySelector(".product-card__swatches");
    this.primaryImage = this.querySelector(".product-card__image > .luc-img > img");
    this.selectedSwatch = null;
    if (this.primaryImage) {
      this.defaultImage = {
        src: this.primaryImage.src,
        srcset: this.primaryImage.srcset,
        alt: this.primaryImage.alt,
      };
    }
    this.events = [
      {
        type: "submit",
        el: this,
        callback: this.submitHandler.bind(this),
      },
    ];
    if (this.swatches && this.primaryImage) {
      this.events.push(
        {
          type: "pointerover",
          el: this.swatches,
          callback: this.previewSwatch.bind(this),
        },
        {
          type: "pointerleave",
          el: this.swatches,
          callback: this.restoreSelectedSwatch.bind(this),
        },
        {
          type: "focusin",
          el: this.swatches,
          callback: this.previewSwatch.bind(this),
        },
        {
          type: "focusout",
          el: this.swatches,
          callback: this.restoreSelectedSwatch.bind(this),
        },
        {
          type: "click",
          el: this.swatches,
          callback: this.selectSwatch.bind(this),
        },
      );
    }
  }

  connectedCallback() {
    handleEventListeners(this.events, "add");
    if (!this.querySelector(".product-form__card")) return;
    this.quantityChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.updateQuantity.bind(this),
    );
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      this.checkCartState.bind(this),
    );
    this.cartChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      this.updateCurrentLine.bind(this),
    );
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
    if (!this.querySelector(".product-form__card")) return;
    this.quantityChangeUnsubscriber();
    this.cartUpdateUnsubscriber();
    this.cartChangeUnsubscriber();
  }

  getSwatch(target) {
    return target.closest("[data-card-swatch]");
  }

  previewSwatch(e) {
    const swatch = this.getSwatch(e.target);
    if (swatch) this.updateCardImage(swatch);
  }

  selectSwatch(e) {
    const swatch = this.getSwatch(e.target);
    if (!swatch) return;

    this.selectedSwatch?.setAttribute("aria-pressed", "false");
    this.selectedSwatch = swatch;
    this.selectedSwatch.setAttribute("aria-pressed", "true");
    this.updateCardImage(this.selectedSwatch);
  }

  restoreSelectedSwatch() {
    if (this.selectedSwatch) {
      this.updateCardImage(this.selectedSwatch);
      return;
    }

    this.primaryImage.src = this.defaultImage.src;
    this.primaryImage.srcset = this.defaultImage.srcset;
    this.primaryImage.alt = this.defaultImage.alt;
  }

  updateCardImage(swatch) {
    this.primaryImage.src = swatch.dataset.imageSrc;
    this.primaryImage.srcset = swatch.dataset.imageSrcset;
    this.primaryImage.alt = swatch.dataset.imageAlt;
  }

  checkCartState({ data }) {
    const { type, response, submitter, product_ids } = data;
    if (type !== "add") return;
    const matching_id = product_ids.filter((id) => {
      return +id === +this.dataset.productId;
    });
    if (this.contains(submitter) || matching_id.length > 0) {
      const matching_line = response.items.filter((item) => {
        return item.id === +this.querySelector('form [name="id"]').value;
      });
      this.currentLine = matching_line[0].key;
      this.querySelector(".product-form__card").classList.add("in-cart");
      this.querySelector('form [name="quantity"]').value = matching_line[0].quantity;
    }
  }

  updateQuantity({ data }) {
    const { quantity, target } = data;

    if (this.contains(target)) {
      this.querySelector('form [name="quantity"]').value = quantity;
      this.querySelector('quantity-selector [name="quantity"]').value = quantity;
      if (target.closest("quantity-selector")) {
        // adjust line item
        this.updateCartLine(quantity, target);
      }
    }
  }

  submitHandler(e) {
    e.preventDefault();
    this.submitForm(e);
  }

  submitForm(e) {
    const formData = new FormData(this.querySelector("form.cart-add__form"));
    const items = [];
    const item = {};
    for (const [key, value] of formData.entries()) {
      item[key] = value;
    }
    item.product_id = this.dataset.productId;
    items.push(item);

    const data = {
      type: "add",
      source: "product-card",
      items,
      submitter: e.submitter,
    };
    if (window.location.pathname.includes("/cart")) {
      const section_id = document.querySelector("main-cart").dataset.section;
      data.sections = [`${section_id}`];
    }
    updateCart(data);
  }

  updateCartLine(quantity, target) {
    if (!this.currentLine) return;
    const data = {
      items: [
        {
          id: `${this.currentLine}`,
          quantity,
          product_id: this.dataset.productId,
        },
      ],
      source: "product-card",
      target,
      type: "update",
    };
    if (window.location.pathname.includes("/cart")) {
      const section_id = document.querySelector("main-cart").dataset.section;
      data.sections = [`${section_id}`];
    }
    updateCart(data);
  }

  updateCurrentLine({ data }) {
    let in_cart = false;
    const { type, response, target, sections, product_ids } = data;
    if (type === "add") return;
    const matching_id = product_ids.filter((id) => {
      return +id === +this.dataset.productId;
    });

    if (this.contains(target) || matching_id.length > 0 || response.items.length > 0) {
      const matching_line = response.items.filter((item) => {
        return item.id === +this.querySelector('form [name="id"]').value;
      });
      if (matching_line.length > 0) {
        in_cart = matching_line[0];
      }
    }
    if (in_cart) {
      this.currentLine = in_cart.key;
      // check source, if === this then ignore
      this.querySelector(".product-form__card").classList.add("in-cart");
      if (!this.contains(target)) {
        this.querySelector('form [name="quantity"]').value = in_cart.quantity;
        this.querySelector("quantity-selector").quantity = in_cart.quantity;
        this.querySelector('quantity-selector [name="quantity"]').value = in_cart.quantity;
      }
    } else {
      this.querySelector(".product-form__card").classList.remove("in-cart");
      this.querySelector('quantity-selector [name="quantity"]').value = this.querySelector(
        '[name="properties[_multiples]"]',
      ).value;
      this.querySelector("quantity-selector").quantity = +this.querySelector(
        '[name="properties[_multiples]"]',
      ).value;
      this.querySelector('form [name="quantity"]').value = this.querySelector(
        '[name="properties[_multiples]"]',
      ).value;
    }
  }
}

customElements.define("product-card", ProductCard);
